import * as React from "react";
import classNames from "classnames";
import Link from "next/link";

import { ErrorBoundary } from "@lululemon/mwa-logger";
import { productEdHandleError } from "../../utils/product-education-logger";
import { Image } from "@lululemon/ui-library";

import ProductEducationAccordions from "./product-education-accordions/product-education-accordions";
import VisualStoryGrid from "../visual-story-grid/visual-story-grid";
import {
  pdpCarouselWWMTImageWidths,
  WWMT_IMG_WIDTHS
} from "../../common/lib/pdp-image-config";

import styles from "./why-we-made-this.module.scss";

// Width/height ratio for the why we made this container
const MOBILE_WIDTH_HEIGHT_WWMT_RATIO = 0.85;
const TABLET_WIDTH_HEIGHT_WWMT_RATIO = 1.75;
const DESKTOP_WIDTH_HEIGHT_WWMT_RATIO = 2.7;

function WhyWeMadeThis({
  activeProductEducationAccordionLinkIdx = 0,
  currentBreakpoint,
  designedFor = "",
  designedForIcon = "",
  deviceType,
  headerBannerHeight = 0,
  materialAndCare = {},
  primaryImage = "",
  prodEduAccordionLinkHasBeenToggled = false,
  productDetailsRef,
  productEducation,
  secondaryImage = "",
  termsUrl,
  termsLinkText,
  whyWeMadeThis,
  wwmtRef,
  hasAccordionImprovements,
  vsgData,
  vsgError,
  vsgVideoRef
}) {
  const wwmtMinHeight = React.useMemo(() => {
    const wwmtOffsetWidth = wwmtRef?.current?.offsetWidth ?? false;

    if (deviceType.isMobile) {
      const dividend = wwmtOffsetWidth ?? WWMT_IMG_WIDTHS.mobile;

      return dividend / MOBILE_WIDTH_HEIGHT_WWMT_RATIO;
    }
    if (deviceType.isTablet) {
      const dividend = wwmtOffsetWidth ?? WWMT_IMG_WIDTHS.tablet;

      return dividend / TABLET_WIDTH_HEIGHT_WWMT_RATIO;
    }
    const dividend = wwmtOffsetWidth ?? WWMT_IMG_WIDTHS.desktop;

    return dividend / DESKTOP_WIDTH_HEIGHT_WWMT_RATIO;
  }, [deviceType, wwmtRef]);

  const oldWWMTSection = (
    <div
      data-testid="why-we-made-this"
      className={classNames(styles.whyWeMadeThis, "row")}
      // Height is set for scroll distance calculations.
      // Needed because of the image's lazy loading
      style={{ minHeight: wwmtMinHeight || "unset" }}
    >
      <div
        className={classNames(styles.textContainer, "col-xs-12 col-sm-6", {
          "col-md-4": secondaryImage,
          "col-md-5": !secondaryImage
        })}
      >
        <h2 className={styles.heading}>
          Why we
          <br />
          made this
        </h2>
        {hasAccordionImprovements && designedFor && (
          <h3
            className={classNames(styles.designedForText, "lll-text-xsmall")}
            data-testid="designed-for-text"
          >
            {designedFor}
          </h3>
        )}
        <p className={styles.text}>
          {whyWeMadeThis.text}
          {!!termsUrl && !!termsLinkText && (
            <span
              className={styles.textTermsConditionsLink}
              data-testid="terms-conditions-link"
            >
              <Link href={termsUrl} legacyBehavior>
                {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                <a target="_blank" className="lll-link-primary">
                  {termsLinkText}
                </a>
              </Link>
            </span>
          )}
        </p>
      </div>

      <div
        className={classNames(styles.imageContainer, "col-xs-12 col-sm-6", {
          "col-md-8": secondaryImage,
          "col-md-7": !secondaryImage
        })}
      >
        {Boolean(primaryImage) && (
          <Image
            alt=""
            className={classNames({
              [styles.image]: secondaryImage,
              [styles.imageSimple]: !secondaryImage
            })}
            containerClassName={styles.imageWrapper}
            sizes={pdpCarouselWWMTImageWidths}
            src={primaryImage}
          />
        )}
        {Boolean(secondaryImage) && !deviceType.isMobile && (
          <Image
            alt=""
            className={styles.image}
            containerClassName={styles.imageWrapper}
            sizes={pdpCarouselWWMTImageWidths}
            src={secondaryImage}
          />
        )}
      </div>
    </div>
  );

  return (
    <ErrorBoundary
      onError={(error) =>
        productEdHandleError(error, "WhyWeMadeThis", "PDP WWMT")
      }
    >
      <div
        className="product-education-container wrapper container-fluid"
        ref={wwmtRef}
      >
        <VisualStoryGrid
          fallback={oldWWMTSection}
          error={vsgError}
          videoRef={vsgVideoRef}
          featureGrid={vsgData?.featureGrid}
          heading={vsgData?.heading}
          heroMedia={vsgData?.heroMedia}
          text={vsgData?.text}
        />
      </div>
      <div>
        <ProductEducationAccordions
          currentBreakpoint={currentBreakpoint}
          useAccordionImprovements={hasAccordionImprovements}
          designedFor={designedFor}
          designedForIcon={designedForIcon}
          headerBannerHeight={headerBannerHeight}
          materialAndCare={materialAndCare}
          activeProductEducationAccordionLinkIdx={
            activeProductEducationAccordionLinkIdx
          }
          prodEduAccordionLinkHasBeenToggled={
            prodEduAccordionLinkHasBeenToggled
          }
          productEducation={productEducation}
          size="large"
          wwmtRef={wwmtRef}
          productDetailsRef={productDetailsRef}
        />
      </div>
    </ErrorBoundary>
  );
}

WhyWeMadeThis.displayName = "WhyWeMadeThis";

export default WhyWeMadeThis;
